/**
 * Toolbar for the tickets list: search + unassigned toggle + filters popover +
 * display options + export + bulk-assign entry point.
 */
import { Download, Filter, Search, SlidersHorizontal, Users } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import {
  FilterBuilder,
  type ColumnDef,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';

export interface TicketListToolbarProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  filterFields: FilterField[];
  query: string;
  onQueryChange: (value: string) => void;
  conditions: FilterCondition[];
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  onApplyFilters: (next: FilterCondition[]) => void;
  visible: Record<string, boolean>;
  onToggleColumn: (id: string, checked: boolean) => void;
  showUnassigned: boolean;
  onToggleUnassigned: (value: boolean) => void;
  selectedIds: number[];
  onBulkAssign: () => void;
  onExport: () => void;
  isExporting: boolean;
}

export function TicketListToolbar<TData>({
  columns,
  filterFields,
  query,
  onQueryChange,
  conditions,
  filterOpen,
  onFilterOpenChange,
  onApplyFilters,
  visible,
  onToggleColumn,
  showUnassigned,
  onToggleUnassigned,
  selectedIds,
  onBulkAssign,
  onExport,
  isExporting,
}: TicketListToolbarProps<TData>) {
  const appliedFieldCount = new Set(conditions.map((c) => c.field)).size;

  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search tickets…"
          startSlot={<Search />}
          style={{ maxWidth: 320 }}
        />
      }
      end={
        <>
          {selectedIds.length > 0 && (
            <Button
              variant="secondary"
              startIcon={<Users size={15} />}
              onClick={onBulkAssign}
            >
              Assign ({selectedIds.length})
            </Button>
          )}

          <Button
            variant={showUnassigned ? 'primary' : 'secondary'}
            onClick={() => onToggleUnassigned(!showUnassigned)}
          >
            Unassigned
          </Button>

          <Popover open={filterOpen} onOpenChange={onFilterOpenChange}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{' '}
                {appliedFieldCount ? (
                  <Badge tone="primary">{appliedFieldCount}</Badge>
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={filterFields}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={onApplyFilters}
                onClose={() => onFilterOpenChange(false)}
              />
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                Display options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
              {columns.map((c) => {
                const id = typeof c.id === 'string' ? c.id : String(c.header ?? '');
                return (
                  <DropdownMenuCheckboxItem
                    key={id}
                    checked={visible[id] !== false}
                    onCheckedChange={(v) => onToggleColumn(id, v === true)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {typeof c.header === 'string' ? c.header : id}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="secondary"
            startIcon={<Download size={15} />}
            onClick={onExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
        </>
      }
    />
  );
}
